import { useTranslation } from 'react-i18next'
import useTracksStore from '../../shared/store/tracks.store'
import VolumeBar from './VolumeBar/VolumeBar.tsx'
import { muteTrack } from '../../shared/store/use-cases/mute-track.ts'
import { soloTrack } from '../../shared/store/use-cases/solo-track.ts'
import {
  setTrackVolume,
  updateTrackVolume,
} from '../../shared/store/use-cases/update-track-volume.ts'
import React, { memo, useEffect, useState } from 'react'
import { useDebounce } from '../../hooks/useDebounce'
import ContextMenu from '../common/ContextMenu.tsx'
import Icon from '../common/Icon.tsx'
import { deleteTrack } from '../../shared/store/use-cases/delete-track.ts'

interface TrackControlsProps {
  trackId: string
  hasOptions?: boolean
}

const TrackControls: React.FC<TrackControlsProps> = ({
  trackId,
  hasOptions,
}) => {
  const { t } = useTranslation()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const track = useTracksStore((state) => state.getTrackState(trackId))

  const debouncedVolume = useDebounce(track.volume, 300)

  useEffect(() => {
    void updateTrackVolume(trackId, debouncedVolume)
  }, [debouncedVolume, trackId])

  return (
    <div className="flex h-[134px] w-[255px] flex-col justify-between border-b border-r border-gray-800 bg-zinc-700 p-4">
      <div className="inline-flex items-center justify-between">
        <h4
          data-testid={`track-heading-${track.type}`}
          className="text-lg font-semibold leading-7 text-white"
        >
          {t(`ls_track_${track.type}`)}
        </h4>
        {hasOptions && (
          <div className="relative">
            <button
              className="relative flex min-h-6 min-w-6 items-center justify-center gap-0.5"
              onClick={() => setIsDropdownOpen(true)}
            >
              <Icon name="menuHorizontal" />
            </button>
            <ContextMenu
              viewModel={[
                {
                  key: 'ls_track_delete_track',
                  action: () => deleteTrack(trackId),
                  icon: <Icon name="xMark" />,
                },
              ]}
              isOpen={isDropdownOpen}
              setIsOpen={setIsDropdownOpen}
              position={{
                right: 0,
                top: 28,
              }}
            />
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <div className="flex items-center">
          <button
            className={`flex h-6 w-6 items-center justify-center border border-gray-900 text-xs font-semibold text-white ${
              track.isMuted || track.isForceMuted
                ? 'bg-blue-500'
                : 'bg-zinc-600'
            } ${track.isMuted || track.isForceMuted ? 'hover:bg-blue-400' : 'hover:bg-zinc-500'} ${
              track.isForceMuted && !track.isMuted
                ? 'animate-[pulse_1s_ease-in-out_infinite] bg-blue-400 hover:bg-blue-500'
                : ''
            }`}
            onClick={() => muteTrack(trackId)}
          >
            M
          </button>
          <button
            className={`flex h-6 w-6 items-center justify-center border border-gray-900 text-xs font-semibold text-white ${
              track.isSolo
                ? 'bg-orange-500 hover:bg-orange-400'
                : 'bg-zinc-600 hover:bg-zinc-500'
            }`}
            onClick={() => soloTrack(trackId)}
          >
            S
          </button>
        </div>
        <VolumeBar
          setVolume={(value) => setTrackVolume(trackId, value)}
          volume={track.volume}
        />
      </div>
    </div>
  )
}

export default memo(TrackControls)
